import omit from 'lodash/omit';

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';

import { ComplianceFeatureFlagService } from '@services/feature-flags/compliance';

import { ASSIGNMENT_FILTER_OPTIONS } from '@app/lib/compliance-utils';

const TRIP_UNIDENTIFIED_OPTIONS = [ASSIGNMENT_FILTER_OPTIONS.UNIDENTIFIED, ASSIGNMENT_FILTER_OPTIONS.AI_IDENTIFIED];
const TRIP_IDENTIFIED_OPTIONS = [
  ASSIGNMENT_FILTER_OPTIONS.AI_ASSIGNED,
  ASSIGNMENT_FILTER_OPTIONS.DRIVER_CLAIMED,
  ASSIGNMENT_FILTER_OPTIONS.MANUALLY_ASSIGNED,
  ASSIGNMENT_FILTER_OPTIONS.DRIVER_CONNECTED,
];

const OLD_SORT_OPTIONS = ['vehicle_number', 'type', 'status', 'driver_name'];
const START_TIME_OPTION = 'start_time';
const DRIVER_ID_KEY = 'driver_id';
const ASSIGNMENT_TYPE_KEY = 'assignment_type';
const ANNOTATION_STATUS = 'annotation_status';
const ANNOTATION_STATUS_MAX_LENGTH = 1;

@Injectable()
export class TripsParamsGuard {
  constructor(private router: Router, private complianceFeatureFlagService: ComplianceFeatureFlagService) {}

  public canActivate(activatedRouteSnapshot: ActivatedRouteSnapshot): boolean {
    const activatedRouteSnapshotData: any = activatedRouteSnapshot.data;
    const config = activatedRouteSnapshotData.tripParamsGuard;
    let params = {...activatedRouteSnapshot.params};
    let url = config.url;
    if (params.id) {
      url += `/${params.id}`;
      params = omit(params,'id');
    }

    if (this.complianceFeatureFlagService.isComplianceTripsNewAILabelsPhaseTwoEnabled()) {
      // Return true if URL doesn't contain old params.
      if (!(params[DRIVER_ID_KEY] && Number(params[DRIVER_ID_KEY]) < 0) &&
        !(params.sort_field && this.checkOldSortOption(params.sort_field))
      ) {
        return true;
      }

      // Check if params contain driver_id, map it to the assignment_type param and then redirect.
      if (params[DRIVER_ID_KEY]) {
        if (params[DRIVER_ID_KEY] === '-1') {
          params[ASSIGNMENT_TYPE_KEY] = `${ASSIGNMENT_FILTER_OPTIONS.UNIDENTIFIED},${ASSIGNMENT_FILTER_OPTIONS.AI_IDENTIFIED}`;
        } else {
          params[ASSIGNMENT_TYPE_KEY] = `${ASSIGNMENT_FILTER_OPTIONS.AI_ASSIGNED},${ASSIGNMENT_FILTER_OPTIONS.MANUALLY_ASSIGNED},
            ${ASSIGNMENT_FILTER_OPTIONS.DRIVER_CLAIMED},${ASSIGNMENT_FILTER_OPTIONS.DRIVER_CONNECTED}`;
          // Regex to remove any white space or new line characters that were being introduced in the string
          params[ASSIGNMENT_TYPE_KEY] = params[ASSIGNMENT_TYPE_KEY].replace(/(\r\n|\n|\r|\s)/gm, '');
        }
        params = omit(params, DRIVER_ID_KEY);
      }

      // Check if URL contains old sort options, default it to start_time and redirect.
      if (params.sort_field && this.checkOldSortOption(params.sort_field)) {
        params.sort_field = START_TIME_OPTION;
      }
      this.router.navigate([url, params]);
      return false;
    } else {
      // Return true if URL doesn't contain new params.
      if (!params[ASSIGNMENT_TYPE_KEY] &&
        !(params.sort_field && params.sort_field !== START_TIME_OPTION && !this.checkOldSortOption(params.sort_field)) &&
        !(params.annotation_status && params.annotation_status.split(',').length > 1)
      ) {
        return true;
      }

      // If params contain assignment_type then map it to driver_id and then redirect.
      if (params[ASSIGNMENT_TYPE_KEY]) {
        const assignmentType: string[] = params[ASSIGNMENT_TYPE_KEY].split(',');
        if (!this.checkMixedAssignmentOptions(assignmentType)) {
          params[DRIVER_ID_KEY] = assignmentType[0] === ASSIGNMENT_FILTER_OPTIONS.UNIDENTIFIED
        || assignmentType[0] === ASSIGNMENT_FILTER_OPTIONS.AI_IDENTIFIED ?
          '-1' : '-2';
        }
        params = omit(params, ASSIGNMENT_TYPE_KEY);
      }

      // If params contain  any sort_field new option, then default it to start_time and redirect.
      if (params.sort_field) {
        if (params.sort_field !== START_TIME_OPTION && !this.checkOldSortOption(params.sort_field)) {
          params.sort_field = START_TIME_OPTION;
        }
      }

      // If params contain any annotation_status, then convert it into array and set the value to the first element of array
      if (params.annotation_status && params.annotation_status.length > ANNOTATION_STATUS_MAX_LENGTH) {
        params = omit(params, ANNOTATION_STATUS);
      }
      this.router.navigate([url, params]);
      return false;
    }
  }

  private checkOldSortOption(selection: string): boolean {
    return OLD_SORT_OPTIONS.includes(selection);
  }

  private checkMixedAssignmentOptions(selection: string[]): boolean {
    return (selection.some((option: ASSIGNMENT_FILTER_OPTIONS) =>
      TRIP_UNIDENTIFIED_OPTIONS.includes(option))
      && selection.some((option: ASSIGNMENT_FILTER_OPTIONS) =>
      TRIP_IDENTIFIED_OPTIONS.includes(option)));
  }
}

